import React from 'react'
import { createRoot } from 'react-dom/client'

import { loadUniques } from 'data/load'
import Store from 'store'
import OverlayRoot from 'ui/utils/OverlayManager'

import { SearchBox } from '@/components/Search'
import { CraftingItem } from '@/components/embeds/CraftingEditor'
import { validateState } from '@/logic/validation'
import { ProfileApiImport } from '@/types/api'
import { CraftingEntry } from '@/types/embed'
import { CraftingProfile, Game, getApiPath } from 'common'
import { SmallIcon } from 'components/Icon'
import { skillItemTypes } from 'components/embeds/SkillGems'
import Select, { SelectOption } from 'ui/select'
import { RangeSlider } from 'ui/slider'
import { searchRegex } from 'utils'
import './Crafting.scss'

const DivineOrb = () => <SmallIcon icon="Currency/CurrencyModValues" title="Divine Orb" />

const renderType = ({ id, name }: SelectOption<number>) => (
  <span>
    <span className="poe-item-type" style={{ backgroundPositionX: `-${id}em` }} /> {name}
  </span>
)

function CraftingTool({ items }: { items: CraftingEntry[] }) {
  const [search, setSearch] = React.useState('')
  const [type, setType] = React.useState(0)
  const maxValue = Math.max(1, ...items.map((item) => item.price || 0))
  const [minPrice, setMinPrice] = React.useState(0)
  const [maxPrice, setMaxPrice] = React.useState(maxValue)

  const getTypeOptions = React.useCallback(() => skillItemTypes.map((name, id) => ({ id, name })), [])

  const searchRe = searchRegex(search, true)
  const filtered = items
    .map((item, index) => ({ ...item, index }))
    .filter((item) => {
      if (type && item.type && type !== item.type) return false
      if (searchRe && !item.name.match(searchRe) && !item.comment.match(searchRe)) return false
      if (item.price && (item.price < minPrice || item.price > maxPrice)) return false
      return true
    })
    .sort((a, b) => {
      const an = a.sortKey || a.name
      const bn = b.sortKey || b.name
      return an.localeCompare(bn)
    })

  return (
    <>
      {/* <p className="dip-gems-notice">
      Welcome to Maxroll's Island tool. Below you can find general Island information in addition to possible rewards you can acquire on each Island.
      To filter for specific rewards, select Rewards in the first drop down, then click "Filter rewards...". Currently the reward items are only up to date for the NA EU beta version.
    </p> */}
      <div className="poe-crafting-options">
        <div className="poe-slot">
          <Select
            options={getTypeOptions}
            value={{ id: type, name: skillItemTypes[type] }}
            onChange={(opt) => opt && setType(opt.id)}
            renderOption={renderType}
            renderValue={renderType}
            className="ui-Select"
          />
        </div>
        <SearchBox search={search} setSearch={setSearch} placeholder="Search Crafting" />
        <div className="poe-price">
          <RangeSlider
            step={1}
            min={0}
            max={maxValue}
            value1={minPrice}
            value2={maxPrice}
            onChange1={setMinPrice}
            onChange2={setMaxPrice}
            className="ui-Slider"
          />
          <div className="poe-label">
            {parseFloat(minPrice.toFixed(1))}-{parseFloat(maxPrice.toFixed(1))} <DivineOrb />
          </div>
        </div>
      </div>
      <div className="poe-crafting-list">
        {filtered.map((item) => (
          <CraftingItem key={item.index} item={item} search={searchRe} />
        ))}
      </div>
    </>
  )
}

document.addEventListener('DOMContentLoaded', async () => {
  const root = document.querySelector('.poeplanner-crafting')
  if (!root) return

  await loadUniques()

  const profile = await validateState(
    JSON.parse(
      ((await fetch(getApiPath(`profiles/${Game}/${CraftingProfile}`)).then((r) => r.json())) as ProfileApiImport).data
    )
  )
  const data = profile?.embeds?.find((e) => e.id === 1)
  if (!data || data.type !== 'crafting') return

  const store = new Store(profile)

  const mxrlMain = root.closest('#mxrl-main') as HTMLElement
  mxrlMain.style.display = 'flex'
  mxrlMain.style.flexDirection = 'column'
  mxrlMain.style.minHeight = 'calc(100vh - 150px)'
  root.classList.add('poeplanner-wrapper')
  createRoot(root).render(
    <Store.Provider store={store}>
      <OverlayRoot>
        <CraftingTool items={data.items} />
      </OverlayRoot>
    </Store.Provider>
  )
})
